import toInteger from 'lodash/toInteger';
import get from 'lodash/get';
import { decamelize } from 'humps';
import pipe from 'lodash/fp/pipe';
import filter from 'lodash/fp/filter';
import map from 'lodash/fp/map';
import flatten from 'lodash/fp/flatten';
import axios from 'axios';
import qs from 'qs';

import { isValidEmail } from 'utils/textFormats';
import { ERROR_REQUIRED, ERROR_INVALID_FORMAT } from 'common/entities/form/errorMessages';
import { TICKET_VALUE_OPEN_PRICE, TICKET_VALUE_QUANTITY, TICKET_EMAIL } from '../constants/ticketTypes';

const MAX_EXCEEDED = 'max_exceeded';
const EMAIL_EXCEEDED_ORDER_LIMIT_MESSAGE = 'This email has exceeded the order limit for this event.';

// VALIDATION

/**
 * Check for errors in ticket quantity
 * @param {Object} quantityValues
 */
const validateQuantity = (quantityValues) => {
    const formValuesWithQuantity = quantityValues
        ? Object.keys(quantityValues).reduce((prev, next) => toInteger(quantityValues[next]) + prev, 0)
        : 0;

    return formValuesWithQuantity !== 0;
};

/**
 * Check if the open prices fit in the limits provided
 * @param {Object} formValues
 * @param {Array} ticketTypes
 * @return {boolean} - are the prices valid
 */
export const validateOpenPrices = (formValues, ticketTypes) => {
    const ticketPrices = formValues[TICKET_VALUE_OPEN_PRICE];
    const ticketQuantities = formValues[TICKET_VALUE_QUANTITY];
    const invalidPrices = Object.entries(ticketQuantities || []).find((ticket) => {
        const ticketQuantity = ticket[1];
        if (toInteger(ticketQuantity) === 0) {
            return false;
        }
        const ticketId = ticket[0];
        const ticketType = ticketTypes && ticketTypes.find((tt) => tt.id === ticketId);
        const ticketPrice = get(ticketPrices, `${ticketId}`, 0);
        const price = parseFloat(ticketPrice) * 100;
        const minOpenPrice = get(ticketType, 'minOpenPrice', 0);
        const maxOpenPrice = get(ticketType, 'maxOpenPrice', 0);

        const isBiggerThanMinPrice = minOpenPrice === 0 ? true : price >= minOpenPrice;
        const isSmallerThanMaxPrice = maxOpenPrice === 0 ? true : price <= maxOpenPrice;
        return !(isBiggerThanMinPrice && isSmallerThanMaxPrice);
    });
    return (invalidPrices || []).length === 0;
};

const specialFields = {
    [TICKET_EMAIL]: (field) => field && !isValidEmail(field) && ERROR_INVALID_FORMAT,
    default: () => false,
};

const validateFormValues = (formValues) =>
    Object.keys(formValues).reduce((prev, fieldName) => {
        const formValue = formValues[fieldName];

        const specialError = (specialFields[fieldName] || specialFields.default)(formValue);

        return {
            ...prev,
            ...(specialError ? { [fieldName]: specialError } : {}),
        };
    }, {});

export const validateFields = (formValues, requiredFields) => {
    const formValuesValidation = validateFormValues(formValues);
    const requiredFieldsValidation = requiredFields
        .filter((requiredField) => !formValues[requiredFields])
        .reduce((prev, next) => ({ ...prev, [next]: ERROR_REQUIRED }), {});

    return {
        ...formValuesValidation,
        ...requiredFieldsValidation,
    };
};

/**
 * Validate errors that affect the entire ticket form
 * @param {Object} formValues
 */
export const validateTopLevelErrors = (formValues, ticketTypes) => {
    const quantityValidation = validateQuantity(formValues[TICKET_VALUE_QUANTITY]);
    const openPriceValidation = validateOpenPrices(formValues, ticketTypes);

    const topLevelErrors = [
        !quantityValidation && 'Please select a valid number of tickets.',
        !openPriceValidation && 'Please ensure price falls between minimum and maximum.',
    ].filter(Boolean);

    return {
        ...(topLevelErrors.length > 0
            ? {
                  0: topLevelErrors,
              }
            : {}),
    };
};

/**
 * Checks the status and returns an empty object or an error
 * @param {string} status
 */
export const validateEmailPerOrderErrors = (status) =>
    status === MAX_EXCEEDED ? { 0: [EMAIL_EXCEEDED_ORDER_LIMIT_MESSAGE] } : {};

/**
 * Validate if there was a previous purchase with this email
 * @param {string} email
 */
export const validateOneOrderPerEmail = (email) =>
    axios({
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        data: qs.stringify({
            action: 'checkEmailLimit',
            email,
        }),
        url: window.location.href,
    });

// SUBMISSION

/**
 * Create a html element to send values in the form submission
 * @param {String} name
 * @param {String} value
 */
export const createFormElement = (name, value) => {
    const element = document.createElement('input');
    element.name = name;

    element.value = value;

    return element;
};

/**
 * Create html elements to send ticket values in the form submission
 * @param {Object} formValues
 */
const createFormGroupValuesElements = (formValues) => {
    const groups = [TICKET_VALUE_OPEN_PRICE, TICKET_VALUE_QUANTITY];

    return pipe(
        filter((groupName) => formValues[groupName]),
        map((groupName) =>
            Object.keys(formValues[groupName]).map((formKey) =>
                createFormElement(`tickets[${formKey}][${decamelize(groupName)}]`, formValues[groupName][formKey]),
            ),
        ),
        flatten,
    )(groups);
};

const createFormValuesElements = (formValues) => {
    const groups = [TICKET_EMAIL];

    return pipe(
        filter((group) => formValues[group]),
        map((group) => createFormElement(group, formValues[group])),
    )(groups);
};

/**
 * Submit the tickets synchronously
 * @param {Object} formValues
 */
export const submitTickets = (formValues, formVersion, formCached) => {
    const form = document.createElement('form');

    const ticketPostUrl = get(window, 'splash.server.constants.ticketPostUrl');
    const action = `${ticketPostUrl}${window.location.search}`;

    form.method = 'POST';
    form.action = action;
    form.target = '_parent';

    const formElements = [
        ...createFormGroupValuesElements(formValues),
        ...createFormValuesElements(formValues),
        createFormElement('ticket_hash', window.splash.server.constants.ticketHash),
        createFormElement('tracking_link_id', window.splash.server.constants.trackingLinkId),
        createFormElement('form_version', formVersion),
        createFormElement('form_cached', formCached ? '1' : '0'),
    ];

    formElements.forEach((element) => {
        form.appendChild(element);
    });

    document.body.appendChild(form);

    form.submit();
};
